import { createFileRoute } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FAQ } from "@/components/FAQ";
import { Reveal } from "@/components/Reveal";
import { Tilt3D } from "@/components/Tilt3D";
import { SITE, whatsappUrl } from "@/lib/site";

export const Route = createFileRoute("/contato")({
  head: () => ({
    meta: [
      { title: "Contato | SVG Multimídia — Especialistas em SEO, Sites e Ficha do Google" },
      { name: "description", content: "Fale com a SVG Multimídia e solicite um diagnóstico para melhorar a presença da sua empresa no Google." },
      { property: "og:title", content: "Contato | SVG Multimídia — Especialistas em SEO, Sites e Ficha do Google" },
      { property: "og:description", content: "Solicite seu diagnóstico de presença no Google." },
      { property: "og:url", content: "/contato" },
    ],
    links: [{ rel: "canonical", href: "https://www.svgmultimidia.com.br/contato" }],
    scripts: [
      {
        children:
          "function gtag_report_conversion(url){var callback=function(){if(typeof(url)!='undefined'){window.location=url;}};gtag('event','conversion',{'send_to':'AW-17558535745/DvBqCICXh5sbEMH8x7RB','event_callback':callback});return false;}",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contato SVG Multimídia",
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "sales",
            areaServed: "BR",
            availableLanguage: "Portuguese",
          },
        }),
      },
    ],
  }),
  component: Contato,
});

function Contato() {
  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-12 pb-12 md:pt-20 md:pb-16">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Contato" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="eyebrow">Fale com a SVG</span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl"><span className="text-gradient">Solicite uma análise inicial</span></h1>
            <p className="mt-4 text-muted-foreground md:text-lg">
              Entenda como sua empresa aparece hoje no Google e quais pontos podem estar impedindo você de receber mais contatos.
            </p>
          </div>
        </div>
      </section>

      <Section>
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr]">
          <Reveal>
            <div className="card-glass space-y-4">
              <h2 className="text-xl font-semibold">Como prefere falar com a gente?</h2>
              <p className="text-sm text-muted-foreground">
                Escolha o canal: respondemos rápido no WhatsApp ou por e-mail.
              </p>
              <a
                href={whatsappUrl("Olá, quero saber mais sobre o CRM SVG")}
                target="_blank"
                rel="noopener"
                className="btn-primary w-full"
                onClick={() => {
                  const w = window as unknown as { gtag_report_conversion?: (u?: string) => boolean };
                  if (typeof w.gtag_report_conversion === "function") w.gtag_report_conversion();
                }}
              >
                Chamar no WhatsApp
              </a>
              <a href={`mailto:${SITE.email}`} className="btn-secondary w-full">
                Enviar e-mail para {SITE.email}
              </a>
            </div>
          </Reveal>

          <div className="space-y-4">
            <Reveal delay={1}>
              <Tilt3D>
                <div className="card-glass">
                  <h2 className="text-lg font-semibold">Chamar no WhatsApp</h2>
                  <p className="mt-2 text-sm text-muted-foreground">A forma mais rápida de falar com a SVG Multimídia.</p>
                  <a href={whatsappUrl()} target="_blank" rel="noopener" className="btn-primary mt-4 w-full">Chamar no WhatsApp</a>
                </div>
              </Tilt3D>
            </Reveal>
            <Reveal delay={2}>
              <Tilt3D>
                <div className="card-glass">
                  <h2 className="text-lg font-semibold">Atendimento</h2>
                  <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                    <li>Email: {SITE.email}</li>
                    <li>Atendemos empresas em todo o Brasil</li>
                    <li>Segunda a sexta · horário comercial</li>
                  </ul>
                </div>
              </Tilt3D>
            </Reveal>
          </div>
        </div>
      </Section>

      <Section>
        <Reveal>
          <div className="mb-6 max-w-2xl">
            <span className="eyebrow">Onde estamos</span>
            <h2 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">Nosso endereço</h2>
            <p className="mt-2 text-sm text-muted-foreground md:text-base">
              Atendemos clientes em todo o Brasil a partir da nossa base. Venha nos visitar ou agende uma reunião online.
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border bg-secondary/40 shadow-lg">
            <iframe
              title="Localização SVG Multimídia no Google Maps"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3543.5752393475505!2d-53.39296752454501!3d-27.357744976384748!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94fb75495d068541%3A0x3e6375ddb83b2096!2sSVG%20Multim%C3%ADdia%20%7C%20Ag%C3%AAncia%20de%20marketing%20digital%20%7C%20Sites%20%7C%20Gest%C3%A3o%20de%20tr%C3%A1fego!5e0!3m2!1spt-BR!2sbr!4v1782458161332!5m2!1spt-BR!2sbr"
              width="100%"
              height="420"
              style={{ border: 0, display: "block" }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        </Reveal>
      </Section>

      <Section>
        <FAQ
          items={[
            { q: "Como funciona o diagnóstico?", a: "Analisamos sua presença atual no Google, sua Ficha, seu site e oportunidades de melhoria. Depois apresentamos um plano claro." },
            { q: "O diagnóstico tem custo?", a: "A análise inicial é sem custo. Aprofundamentos e execuções são propostos depois." },
            { q: "Vocês atendem fora do Brasil?", a: "Nosso foco é empresas brasileiras. Casos específicos podem ser conversados." },
          ]}
        />
      </Section>
    </>
  );
}